/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument */
// @ts-nocheck
import sortedIndexBy from 'lodash/sortedIndexBy';
import debounce from 'lodash/debounce';

import { DefaultService } from 'gen/stream-api/requests/services.gen';
import { CandleTOHLCV } from 'gen/stream-api/requests/types.gen';
import {
  FetchAuxOptions,
  CandleTupleTOHLCV,
  CandleTupleSTOHLC,
  candleSTOHLCfromTuple,
  SymbolLineKind,
  SymbolLineParams,
  nameFromSymbolLineParams,
  RawHiroAndPrice,
  HiroAndPrice,
  HiroAlertCategoryMap,
  AlertData,
  HiroOverview,
} from 'types';
import {
  getEquitiesBySyms,
  hiroV2HistoryLimit,
  streamTypeFromSymbolLineParams,
  symbolLineParamsFromStreamType,
} from 'util/hiro';
import { dayjs, ET } from 'util/shared';
import { captureExceptionPostHog } from 'util/shared/posthog';
import { SendStreamControlMsgFn } from 'hooks/bloomberg/streaming/useStreamingSocket';
import { StreamType } from 'types/shared';
import { REVERSE_FIELD_NAME_MAPPING } from 'config/equityhub';
import { unsupressedSetInterval } from 'util/poll';
import { CANDLE_DURATION_OPTIONS } from 'types/hiro';
import { RemotePollingWorker } from 'types/pollingWorker';

import { Dayjs } from 'dayjs';

import { convertData } from './HiroChart/convertData';
import { HiroContextValue } from './HiroProvider';
import {
  isMarketOpenOnDate,
  MILLISECONDS_PER_DAY,
  sleep,
} from 'util/shared/date';
import { skipNonMarketHours } from 'components/sg_d3fc/skip_non_market_hours';

export const HISTORY_BEGIN = dayjs('2025-04-08T00:00:00Z');

export const MINUTES_IN_DAY = 24 * 60;

export const DEFAULT_CANDLE_DURATION = 5 * 60 * 1_000;
const GET_BARS_RECURSION_LIMIT = 6;
const FIXUP_STREAMING_CANDLES_INTERVAL = 60 * 1_000;

// For each item, if the visible range is above the first element, then the
// candle length must be at least the duration of the second element.
const VISIBLE_DAYS_TO_MIN_CANDLE_DURATION_MS = [
  [0.6, 60 * 1_000],
  [1.1, 5 * 60 * 1_000],
  [2.1, 10 * 60 * 1_000],
  [3.1, 15 * 60 * 1_000],
  [5.1, 30 * 60 * 1_000],
];

// on a full 4k screen, each candle is 3 pixels wide
const MAX_CANDLES_IN_VIEW = 1_565;

const MAX_VISIBLE_DAYS =
  VISIBLE_DAYS_TO_MIN_CANDLE_DURATION_MS[
    VISIBLE_DAYS_TO_MIN_CANDLE_DURATION_MS.length - 1
  ][0];

type CandleTOHLCVSDG = CandleTOHLCV & {
  // Represents the same time as `time`, but simply in `Date` format
  date: Date;
  // A monotonically increasing number that is used to indicate the recency of a
  // received candle is. This enables the updating of the in-memory candle array with
  // new candles as they come in and as they become more correct/more canonical.
  // This is due to the out-of-order nature of candles being parsed from dxfeed
  candleGeneration?: number;
};

interface ChunkWindow {
  beginUnixTsInclusive: number;
  endUnixTsExclusive: number;
}

export class SignalSeries {
  symbolLineParams: SymbolLineParams;

  // a contiguous array of candles from the server or accumulated from streaming
  candles: CandleTOHLCVSDG[] = [];

  // the actual candles sent to d3, manipulated and accumulated according to
  // the user inputs
  mutatedCandles: CandleTOHLCVSDG[] = [];

  // the range requested from the server, which may return
  // results that don't start and end _exactly_ at the
  // requested boundaries
  requestedRangeBeg?: number;
  requestedRangeEnd?: number;

  latestFetches: Promise<void> = Promise.resolve();

  // stores the rolling accumulation number of the current window for this
  // series
  windowAccumulator?: number;

  // stores the current window's begin time
  windowBeginUnixTs?: number;
  windowAccumulatorTs?: number;

  stopFixupRecentCandlesInterval?: () => void;

  // the last timestamp that a fixup request for this series completed with data
  tsLastSuccessfulFixup?: dayjs.Dayjs;

  constructor(symbolLineParams: SymbolLineParams) {
    this.symbolLineParams = symbolLineParams;
  }

  teardown() {
    this.stopFixupRecentCandlesInterval?.();
  }
}

export interface FetchRange {
  rangeBeg: dayjs.Dayjs;
  rangeEnd: dayjs.Dayjs;
}

interface GetFetchRangesResult {
  fetchRanges: FetchRange[];
  beg: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

interface PeriodParams {
  from: dayjs.Dayjs;
  to: dayjs.Dayjs;
}

interface VisibleTimeRange {
  from: dayjs.Dayjs;
  to: dayjs.Dayjs;
}

export class HiroChartDatafeed {
  fetchAPIWithLog?: (
    endpoint: string,
    auxiliaryOptions?: FetchAuxOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;

  candleDurationMs: number = DEFAULT_CANDLE_DURATION;
  userSpecifiedCandleDurationMs: number = DEFAULT_CANDLE_DURATION;

  beginVisibleRange: dayjs.Dayjs;
  endVisibleRange?: dayjs.Dayjs; // if unset, corresponds to now

  windowBegin?: number;
  rollingWindowMinutes: number = MINUTES_IN_DAY;

  signalSeries: Record<string, SignalSeries> = {};
  sendStreamControlMsg?: SendStreamControlMsgFn;

  currentSession: 'extended' | 'regular';
  lastDataReset: dayjs.Dayjs = dayjs();
  broadCastTimeRangeUpdate?: (update: HiroTimeRange) => void;
  broadCastPriceUpdate?: (
    sym: string,
    price: number,
    timestamp: number,
  ) => void;
  broadCastHiroAllUpdate?: (
    sym: string,
    hiro: number,
    timestamp: number,
  ) => void;
  updateHiroCtx?: (u: Partial<HiroContextValue>) => void;

  currentGlobalStocksState?: Map<string, HiroOverview>;

  candleGeneration: number = 0;

  debouncedRerenderChart: () => void;
  debouncedOnVisibleRangeChanged: (range: VisibleTimeRange) => void;

  boundEnableSignalSeries: (symbolLineParams: SymbolLineParams) => void;
  boundDisableSignalSeries: (symbolLineParams: SymbolLineParams) => void;

  webWorker?: RemotePollingWorker;
  isStreamOpen?: () => boolean;
  webWorkerExistsPromise: Promise<void>;
  webWorkerExistsPromiseResolver: () => void;
  resetZoomRef: Ref;

  constructor(
    rollingWindowMinutes?: number,
    setHiroAlerts: (v: AlertCategory[]) => void,
    rerenderChart: () => void,
    currentSession: 'extended' | 'regular',
    resetZoomRef,
  ) {
    this.currentSession = currentSession;
    if (rollingWindowMinutes > 0) {
      this.rollingWindowMinutes = rollingWindowMinutes;
    }

    this.debouncedRerenderChart = debounce(rerenderChart, 150);
    const now = dayjs().tz(ET);
    this.beginVisibleRange =
      currentSession === 'extended'
        ? dayjs().tz(ET).startOf('day')
        : dayjs().tz(ET).hour(9).minute(30).second(0).millisecond(0);
    while (
      this.beginVisibleRange > now ||
      !isMarketOpenOnDate(this.beginVisibleRange)
    ) {
      this.beginVisibleRange = this.beginVisibleRange.subtract(1, 'day');
    }
    this.boundEnableSignalSeries = this.enableSignalSeries.bind(this);
    this.boundDisableSignalSeries = this.disableSignalSeries.bind(this);
    this.debouncedOnVisibleRangeChanged = debounce(
      (range: VisibleTimeRange) => this.onVisibleRangeChanged(range),
      350,
      { maxWait: 750 },
    );

    const {
      promise: webWorkerExistsPromise,
      resolve: webWorkerExistsPromiseResolver,
    } = Promise.withResolvers<void>();
    this.webWorkerExistsPromise = webWorkerExistsPromise;
    this.webWorkerExistsPromiseResolver = webWorkerExistsPromiseResolver;
    this.resetZoomRef = resetZoomRef;
  }

  setWebWorker(webWorker: RemotePollingWorker) {
    this.webWorker = webWorker;
    this.webWorkerExistsPromiseResolver();
  }

  enableSignalSeries(symbolLineParams: SymbolLineParams, refetch?: boolean) {
    const seriesName = nameFromSymbolLineParams(symbolLineParams);
    if (this.signalSeries[seriesName] != null && !(refetch ?? false)) {
      return;
    }
    void this.getBars(symbolLineParams, {
      from: this.beginVisibleRange,
      to: this.endVisibleRange,
    }).then(() => {
      if (
        this.endVisibleRange == null &&
        this.signalSeries[seriesName] != null
      ) {
        this.subscribeBars(symbolLineParams);
      }
    });
  }

  disableSignalSeries(symbolLineParams: SymbolLineParams) {
    const seriesName = nameFromSymbolLineParams(symbolLineParams);
    if (this.signalSeries[seriesName] == null) {
      return;
    }
    if (this.endVisibleRange == null) {
      this.unsubscribeBars(symbolLineParams);
    }
    this.signalSeries[seriesName].teardown();
    delete this.signalSeries[seriesName];
  }

  /// Get the beginning of the rolling window that contains the given timestamp
  windowFromUnixTs(
    unixTimestamp: number,
    rollingWindowMinutes?: number,
  ): ChunkWindow {
    const rollingMinutes = rollingWindowMinutes ?? this.rollingWindowMinutes;
    let beginUnixTsInclusive = 0;
    if (!isFinite(rollingMinutes) || rollingMinutes >= MINUTES_IN_DAY) {
      let begin = dayjs
        .unix(
          rollingMinutes > MINUTES_IN_DAY && unixTimestamp > this.windowBegin
            ? this.windowBegin
            : unixTimestamp,
        )
        .tz(ET);
      if (this.currentSession === 'extended') {
        begin = begin.startOf('date');
      } else {
        if (begin < begin.hour(9).minute(30).second(0).millisecond(0)) {
          begin = begin.subtract(1, 'day');
        }
        begin = begin.hour(9).minute(30).second(0).millisecond(0);
      }

      beginUnixTsInclusive = begin.unix();
    } else {
      beginUnixTsInclusive =
        Math.floor(unixTimestamp / (rollingMinutes * 60)) * rollingMinutes * 60;
    }
    const endUnixTsExclusive = beginUnixTsInclusive + rollingMinutes * 60;
    return {
      beginUnixTsInclusive,
      endUnixTsExclusive,
    };
  }

  getOrCreateSeries(symbolLineParams: SymbolLineParams): SignalSeries {
    const seriesName = nameFromSymbolLineParams(symbolLineParams);
    return (this.signalSeries[seriesName] =
      this.signalSeries[seriesName] ?? new SignalSeries(symbolLineParams));
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async getBars(
    symbolLineParams: SymbolLineParams,
    periodParams: PeriodParams,
    recursiveCnt?: number,
    isRefetch?: boolean,
    anyFetchesFailed?: () => void,
  ): Promise<void> {
    this.candleGeneration += 1;
    const candleGeneration = this.candleGeneration;
    if (recursiveCnt >= GET_BARS_RECURSION_LIMIT) {
      return;
    }

    const historyLimit = hiroV2HistoryLimit();
    if (dayjs.unix(periodParams.to) < historyLimit) {
      return;
    }
    if (periodParams.from < historyLimit.unix()) {
      periodParams.from = historyLimit.unix();
    }

    const now = dayjs().tz(ET);

    const series = this.getOrCreateSeries(symbolLineParams);

    await series.latestFetches;

    const { fetchRanges, beg, end } =
      (isRefetch ?? false)
        ? {
            fetchRanges: [
              {
                rangeBeg: periodParams.from,
                rangeEnd: periodParams.to,
              },
            ],
            beg: periodParams.from,
            end: periodParams.to,
          }
        : this.getFetchRanges(series, periodParams, now);

    const fetchStartedAt = performance.now();
    const candlesReqs = this.getCandleReqsFromFetchRanges(
      fetchRanges,
      end,
      now,
      symbolLineParams,
      anyFetchesFailed,
    );

    series.latestFetches = Promise.all(candlesReqs)
      .then((responses) => {
        responses.forEach((rsp: { candles: CandleTupleTOHLCV[] }) => {
          if (!rsp.candles.length) {
            return;
          }

          const candles: CandleTOHLCVSDG[] = rsp.candles.map(
            ([time, open, high, low, close, volume]: CandleTupleTOHLCV) => ({
              time,
              date: new Date(time),
              open,
              high,
              low,
              close,
              volume,
              candleGeneration,
            }),
          );
          if (candles[0].time < series.candles[0]?.time) {
            [].push.apply(candles, series.candles);
            series.candles = candles;
          } else {
            [].push.apply(series.candles, candles);
          }
          series.candles.sort(
            (a, b) =>
              a.time - b.time ||
              // if times match, order most recently fetched candles first for
              // duplicate removal in the next step
              (b?.candleGeneration ?? 0) - (a?.candleGeneration ?? 0),
          );

          // remove duplicates, if any.  Possibly caused by concurrent requests
          series.candles = series.candles.filter(
            (candle, index, self) =>
              index == 0 || candle.time !== self[index - 1].time,
          );
          series.requestedRangeBeg = beg.valueOf();
          series.requestedRangeEnd = end.valueOf();
        });

        const { beginUnixTsInclusive: periodFirstWindowBeg } =
          this.windowFromUnixTs(periodParams.from.unix());

        const periodEnd =
          this.windowFromUnixTs(periodParams.to?.unix()).beginUnixTsInclusive +
          this.rollingWindowMinutes * 60;

        let periodFirstWindowIdx =
          sortedIndexBy(
            series.candles,
            { time: periodFirstWindowBeg } as CandleTOHLCVSDG,
            'time',
          ) ?? 0;
        periodFirstWindowIdx = Math.max(periodFirstWindowIdx, 0);

        const periodEndIdx =
          sortedIndexBy(
            series.candles,
            { time: periodEnd * 1_000 } as CandleTOHLCVSDG,
            'time',
          ) ?? 0;

        let lastClose = 0;
        let items = [];
        if (symbolLineParams.kind === 'price') {
          items = series.candles.slice(
            periodFirstWindowIdx,
            Math.min(series.candles.length, periodEndIdx),
          );
        } else if (symbolLineParams.kind === 'hiro') {
          let curWindow: ChunkWindow | undefined = undefined;
          for (
            let i = periodFirstWindowIdx;
            i <= periodEndIdx && i < series.candles.length;
            i++
          ) {
            const c = series.candles[i];

            const tsUnix = c.time / 1_000;
            if (
              curWindow == null ||
              !(
                curWindow.beginUnixTsInclusive <= tsUnix &&
                curWindow.endUnixTsExclusive > tsUnix
              )
            ) {
              lastClose = 0;
              curWindow = this.windowFromUnixTs(tsUnix);
            }

            const result = {
              time: c.time,
              date: c.date,
              candleGeneration: c.candleGeneration,
              open: lastClose + c.open,
              high: lastClose + c.high,
              low: lastClose + c.low,
              close: lastClose + c.close,
              volume: c.volume,
            };
            lastClose = result.close;
            series.windowAccumulator = lastClose;
            series.windowBeginUnixTs = curWindow.beginUnixTsInclusive;
            series.windowAccumulatorTs = result.time;

            items.push(result);
          }
        }
        if (items.length) {
          // splice new items into series array
          const beg =
            sortedIndexBy(series.mutatedCandles, items[0], 'time') ?? 0;
          const end =
            sortedIndexBy(
              series.mutatedCandles,
              items[items.length - 1],
              'time',
            ) ?? series.length - 1;

          series.mutatedCandles.splice(beg, Math.max(0, end - beg), ...items);
          series.mutatedCandles.sort(
            (a, b) =>
              a.time - b.time ||
              // if times match, order most recently fetched candles first for
              // duplicate removal in the next step
              (b?.candleGeneration ?? 0) - (a?.candleGeneration ?? 0),
          );
          series.mutatedCandles = series.mutatedCandles.filter(
            (candle, index, self) =>
              index == 0 || candle.time !== self[index - 1].time,
          );
          this.debouncedRerenderChart();
        }
      })
      // Without this, a dropped connection emitted one uncaught
      // `TypeError: Failed to fetch` per poll tick, per series.
      // Re-send to PostHog for visibility.
      .catch((err: unknown) => {
        console.error(
          `HIRO candle fetch failed for ${symbolLineParams.symbol}`,
          err,
        );
        captureExceptionPostHog(err, {
          symbol: symbolLineParams.symbol,
          kind: symbolLineParams.kind,
          source: 'hiro_candle_fetch',
          is_refetch: isRefetch ?? false,
          elapsed_ms: Math.round(performance.now() - fetchStartedAt),
          online: navigator.onLine,
          visibility: document.visibilityState,
          stream_open: this.isStreamOpen?.() ?? null,
        });
      });
  }

  subscribeBars(symbolLineParams: SymbolLineParams): void {
    const seriesName = nameFromSymbolLineParams(symbolLineParams);
    // Never recreate a series here. One that was disabled would come back
    // with a poller that nothing tears down.
    const series = this.signalSeries[seriesName];
    if (
      series == null ||
      series.symbolLineParams.kind === 'key level' ||
      series.symbolLineParams.kind == 'alert' ||
      series.symbolLineParams.kind == 'oldhiro'
    ) {
      return;
    }
    if (
      series.symbolLineParams.kind == 'price' ||
      series.symbolLineParams.kind == 'hiro'
    ) {
      this.subscribeSeriesStreamCandles(series);
      void this.webWorkerExistsPromise.then(() => {
        if (this.signalSeries[seriesName] !== series) {
          return;
        }
        series.stopFixupRecentCandlesInterval?.();
        series.stopFixupRecentCandlesInterval = unsupressedSetInterval(
          this.webWorker,
          () => {
            // Stopping goes through the worker, so a tick can still land
            // after teardown
            if (this.signalSeries[seriesName] !== series) {
              return;
            }
            console.log(
              'fixup ',
              nameFromSymbolLineParams(series.symbolLineParams),
            );
            const to = dayjs().tz(ET).startOf('minute');
            let from = to.subtract(
              FIXUP_STREAMING_CANDLES_INTERVAL * 3,
              'milliseconds',
            );
            if (
              (series.tsLastSuccessfulFixup ?? 0) > 0 &&
              series.tsLastSuccessfulFixup < from
            ) {
              from = dayjs(series.tsLastSuccessfulFixup)
                .tz(ET)
                .subtract(FIXUP_STREAMING_CANDLES_INTERVAL * 3, 'milliseconds')
                .startOf('hour');
            }
            let anyFetchesFailed = false;

            void this.getBars(
              series.symbolLineParams,
              {
                from,
                to,
              },
              0,
              true,
              () => {
                series.tsLastSuccessfulFixup = dayjs(+to).subtract(1, 'hours');
                anyFetchesFailed = true;
              },
            ).then(async () => {
              await series.latestFetches;

              // the promise tree is not properly waited on.  This sleep accounts
              // for bad fetches resolving and setting `anyFetchesFailed` to true
              // that are not properly waited on by `series.latestFetches`
              await sleep(2_000);
              if (!anyFetchesFailed) {
                series.tsLastSuccessfulFixup = dayjs(
                  Math.max(series.tsLastSuccessfulFixup ?? 0, to.valueOf()),
                );
              } else {
                series.tsLastSuccessfulFixup = dayjs(+to).subtract(1, 'hours');
              }
              if (this.endVisibleRange == null) {
                this.resetZoomRef.current?.(true);
              }
            });
          },
          FIXUP_STREAMING_CANDLES_INTERVAL,
        );
      });
    }
  }

  subscribeSeriesStreamCandles(series: SignalSeries): void {
    this.sendStreamControlMsg!({
      action: 'subscribe',
      underlyings: [series.symbolLineParams.symbol],
      stream_types: streamTypeFromSymbolLineParams(series.symbolLineParams),
    });
  }

  unsubscribeBars(symbolLineParams: SymbolLineParams): void {
    const seriesName = nameFromSymbolLineParams(symbolLineParams);
    const series = Object.values(this.signalSeries).find(
      (series) =>
        nameFromSymbolLineParams(series.symbolLineParams) === seriesName,
    );
    if (series != null) {
      if (series.symbolLineParams.kind !== 'price') {
        return;
      }
      this.sendStreamControlMsg!({
        action: 'unsubscribe',
        underlyings: [series.symbolLineParams.symbol],
        stream_types: streamTypeFromSymbolLineParams(series.symbolLineParams),
      });
      series.stopFixupRecentCandlesInterval?.();
    }
  }

  async listenCandlesStream(
    streamCandle: CandleTupleSTOHLC,
    streamType: StreamType,
  ) {
    const candleSTOHLC = candleSTOHLCfromTuple(streamCandle);
    const { symbol, open, high, low, close } = candleSTOHLC;
    let time = Number(candleSTOHLC.time);
    const lineParams = symbolLineParamsFromStreamType(symbol, streamType);
    if (lineParams.kind === 'hiro' || lineParams.kind === 'price') {
      time = Math.floor(time / this.candleDurationMs) * this.candleDurationMs;
    }

    let candle = {
      time,
      date: new Date(time),
      open,
      high,
      low,
      close,
      candleGeneration: this.candleGeneration,
    };

    const seriesName = nameFromSymbolLineParams(lineParams);
    const series = this.signalSeries[seriesName];
    if (series == null) {
      // ignoring candle that we don't care about
      return;
    }

    await series.latestFetches;

    if (lineParams.kind === 'price') {
      const lastCandle = series.candles[series.candles.length - 1];
      if (lastCandle?.time === candle.time) {
        lastCandle.close = candle.close;
        lastCandle.high = Math.max(lastCandle.high, candle.high);
        lastCandle.low = Math.min(lastCandle.high, candle.low);
      } else {
        series.candles.push(candle);
      }
      this.broadCastPriceUpdate?.(lineParams.symbol, candle.close, time);
    } else if (lineParams.kind === 'hiro') {
      // delta candles
      const lastCandle = series.candles[series.candles.length - 1];
      if (lastCandle == null || series.windowAccumulator == null) {
        return;
      }

      const { beginUnixTsInclusive: thisWindowBegin } = this.windowFromUnixTs(
        candle.time / 1_000,
      );

      if (lastCandle.time !== series.windowAccumulatorTs) {
        // recalculate window accumulator
        const { beginUnixTsInclusive: lastWindowBegin } = this.windowFromUnixTs(
          lastCandle.time / 1_000,
        );
        let periodFirstWindowIdx =
          sortedIndexBy(
            series.candles,
            { time: lastWindowBegin } as CandleTOHLCVSDG,
            'time',
          ) ?? 0;
        periodFirstWindowIdx = Math.max(periodFirstWindowIdx, 0);
        let windowAccumulator = 0;

        let curWindow: ChunkWindow | undefined = undefined;
        for (let i = periodFirstWindowIdx; i < series.candles.length; i++) {
          const c = series.candles[i];
          const tsUnix = c.time / 1_000;
          if (
            curWindow == null ||
            !(
              curWindow.beginUnixTsInclusive <= tsUnix &&
              curWindow.endUnixTsExclusive > tsUnix
            )
          ) {
            windowAccumulator = 0;
            curWindow = this.windowFromUnixTs(tsUnix);
          }
          windowAccumulator += c.close;
        }
        series.windowAccumulator = windowAccumulator;
        series.windowAccumulatorTs = lastCandle.time;
        series.windowBeginUnixTs = curWindow.beginUnixTsInclusive;
      }

      if (lastCandle.time >= candle.time) {
        // unwind window accumulator to be used to re-calculate the windows value
        series.windowAccumulator = series.windowAccumulator - lastCandle.close;

        lastCandle.high = Math.max(
          lastCandle.high,
          lastCandle.close + candle.high,
        );
        lastCandle.low = Math.min(
          lastCandle.low,
          lastCandle.close + candle.low,
        );
        lastCandle.close += candle.close;
        candle = lastCandle;
      } else {
        if (series.windowBeginUnixTs != thisWindowBegin) {
          series.windowAccumulator = 0;
          series.windowAccumulatorTs = candle.time;
          series.windowBeginUnixTs = thisWindowBegin;
        }
        series.candles.push(candle);
      }
      // now going to be mutated and accumulated
      candle = { ...candle };

      candle.open += series.windowAccumulator;
      candle.high += series.windowAccumulator;
      candle.low += series.windowAccumulator;
      candle.close += series.windowAccumulator;
      series.windowAccumulator = candle.close;

      if (
        this.rollingWindowMinutes == MINUTES_IN_DAY &&
        lineParams.deltaLineParams.optionAccumulationType == 'Total' &&
        lineParams.deltaLineParams.optionOrigin == 'All' &&
        lineParams.deltaLineParams.isFiltered == true
      ) {
        this.broadCastSignalUpdate?.(
          lineParams.symbol,
          series.windowAccumulator,
          time,
        );
      }
    }

    // update the global ticker state, if applicable
    this.currentGlobalStocksState?.get(lineParams.symbol);

    const lastExistingCandleTime =
      series.mutatedCandles[series.mutatedCandles.length - 1]?.time;
    if (lastExistingCandleTime === candle.time) {
      series.mutatedCandles[series.mutatedCandles.length - 1] = candle;
    } else if (lastExistingCandleTime < candle.time) {
      series.mutatedCandles.push(candle);
    }
    this.debouncedRerenderChart();
  }

  reRegisterCandleSubscriptions(
    _sendStreamControlMsg: SendStreamControlMsgFn,
  ): void {
    for (const series of Object.values(this.signalSeries)) {
      switch (series.symbolLineParams.kind) {
        case 'key level':
        case 'alert':
        case 'oldhiro':
        case 'hiro':
          continue;
      }
      this.subscribeSeriesStreamCandles(series);
    }
  }

  getFetchRanges(
    series: SignalSeries,
    periodParams: PeriodParams,
    now: dayjs.Dayjs,
  ): GetFetchRangesResult {
    let windowBegin = this.windowBegin;

    if (windowBegin == null) {
      windowBegin = this.windowFromUnixTs(
        (this.beginVisibleRange ?? periodParams.from).unix(),
      ).beginUnixTsInclusive;
    }

    let maxFetchTime = this.endVisibleRange?.add(1, 'day');
    if (maxFetchTime == null || maxFetchTime > now) {
      maxFetchTime = now;
    }

    let beg = dayjs
      .unix(
        this.windowFromUnixTs(periodParams.from.unix(), MINUTES_IN_DAY)
          .beginUnixTsInclusive,
      )
      .tz(ET);

    if (beg < HISTORY_BEGIN) {
      beg = HISTORY_BEGIN;
    }

    let end = periodParams.to ?? now;
    const window = this.windowFromUnixTs(now.unix(), MINUTES_IN_DAY);
    if (end.unix() < window.beginUnixTsInclusive) {
      end = dayjs.unix(window.endUnixTsExclusive);
    }
    if (end > maxFetchTime) {
      end = maxFetchTime;
    }
    end = end.tz(ET);

    // TODO - remove when chart more solid
    // console.log(
    //   'RANGE ' + series.name,
    //   '\n   periodParams.from=',
    //   dayjs.unix(periodParams.from).format(),
    //   '\n     periodParams.to=',
    //   dayjs.unix(periodParams.to).format(),
    //   '\n                 beg=',
    //   beg.format(),
    //   '\n                 end=',
    //   end.format(),
    // );

    // check for non-overlapping/abutting ranges
    if (
      (series.requestedRangeBeg != null &&
        end.valueOf() < series.requestedRangeBeg - this.candleDurationMs) ||
      (series.requestedRangeEnd != null &&
        beg.valueOf() > series.requestedRangeEnd + this.candleDurationMs)
    ) {
      series.candles = [];
      series.requestedRangeBeg = undefined;
      series.requestedRangeEnd = undefined;
    }

    const fetchRanges = [];
    if (series.candles.length == 0) {
      fetchRanges.push({ rangeBeg: beg, rangeEnd: end });
    } else {
      if (beg.valueOf() < series.requestedRangeBeg!) {
        fetchRanges.push({
          rangeBeg: beg,
          rangeEnd: dayjs(series.requestedRangeBeg).tz(ET),
        });
      }
      if (end.valueOf() > series.requestedRangeEnd!) {
        fetchRanges.push({
          rangeBeg: dayjs(
            this.windowFromUnixTs(series.requestedRangeEnd)
              .beginUnixTsInclusive,
          ).tz(ET),
          rangeEnd: end,
        });
      }
    }
    return { fetchRanges, beg, end };
  }

  getCandleReqsFromFetchRanges(
    fetchRanges: FetchRange[],
    end: dayjs.Dayjs,
    now: dayjs.Dayjs,
    symbolLineParams: SymbolLineParams,
    anyFetchesFailed?: () => void,
  ): Promise<CandleTOHLCVSDG[]>[] {
    const candlesReqs = [];
    // chunk requests into day boundaries to enable increased client caching
    const anyFetchesFailedWrpr = (e) => {
      anyFetchesFailed?.();
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(e);
    };

    for (const { rangeBeg: reqBeg, rangeEnd: reqEnd } of this.rangeSubFetches(
      fetchRanges,
      symbolLineParams.kind,
    )) {
      if (symbolLineParams.kind === 'price') {
        candlesReqs.push(
          DefaultService.getPrices({
            symbol: symbolLineParams.symbol,
            timeGte: reqBeg.tz(ET).format(),
            timeLt: reqEnd.tz(ET).format(),
            limit: 100_000,
            candleDurationMs: this.candleDurationMs,
          }).catch(anyFetchesFailedWrpr),
        );
      } else if (symbolLineParams.kind === 'hiro') {
        candlesReqs.push(
          DefaultService.getDeltaSignal({
            isFiltered: symbolLineParams.deltaLineParams.isFiltered,
            optionAccumulationType:
              symbolLineParams.deltaLineParams.optionAccumulationType,
            optionOrigin: symbolLineParams.deltaLineParams.optionOrigin,
            symbol: symbolLineParams.symbol,
            timeGte: reqBeg.tz(ET).format(),
            timeLt: reqEnd.tz(ET).format(),
            limit: 100_000,
            candleDurationMs: this.candleDurationMs,
          }).catch(anyFetchesFailedWrpr),
        );
      } else if (symbolLineParams.kind === 'oldhiro') {
        const reqBegOfDay = reqBeg.tz(ET).startOf('day');
        candlesReqs.push(
          this.fetchAPIWithLog<Record<string, Record<string, RawHiroAndPrice>>>(
            `v11/hiro?all=1&nextExp=1&retail=1&syms=${encodeURIComponent(symbolLineParams.symbol)}&start=${reqBegOfDay.format('YYYY-MM-DD')}&end=${reqBegOfDay.format('YYYY-MM-DD')}`,
          )
            .then((data: Record<string, Record<string, RawHiroAndPrice>>) => {
              let candles: HiroAndPrice[] = [];
              const symData = data[symbolLineParams.symbol];
              if (symData != null) {
                const convertedData = convertData(symData, true, ET);
                const originData =
                  convertedData[
                    symbolLineParams.deltaLineParams.optionOrigin === 'All'
                      ? 'all'
                      : 'nextExp'
                  ];
                if (originData != null) {
                  candles =
                    originData[
                      symbolLineParams.deltaLineParams
                        .optionAccumulationType === 'Total'
                        ? 'TOT'
                        : symbolLineParams.deltaLineParams
                              .optionAccumulationType === 'Calls'
                          ? 'C'
                          : 'P'
                    ] ?? [];
                }
              }
              return {
                candles: candles.map((c: HiroAndPrice) => [
                  c.utc_time,
                  c.mid_signal,
                  c.mid_signal,
                  c.mid_signal,
                  c.mid_signal,
                  0,
                ]),
              };
            })
            .catch(anyFetchesFailedWrpr),
        );
      } else if (symbolLineParams.kind === 'key level') {
        const reqBegOfDay = reqBeg.tz(ET).startOf('day');
        candlesReqs.push(
          getEquitiesBySyms(reqBegOfDay, symbolLineParams.symbol)
            .then((rsp) => {
              if (rsp == null) {
                return { candles: [] };
              }
              const v = rsp[
                REVERSE_FIELD_NAME_MAPPING[symbolLineParams.keyLevel]
              ] as number;
              const candles = [];
              const candleVals = [v, v, v, v];
              const alertBegTime =
                this.currentSession === 'extended'
                  ? reqBegOfDay
                  : reqBegOfDay.hour(9).minute(30).second(0);
              let endOfAlert =
                this.currentSession === 'extended'
                  ? reqBegOfDay.add(1, 'day').startOf('day')
                  : reqBegOfDay.hour(16).minute(0).second(0);

              if (endOfAlert > dayjs()) {
                // get the last candle time from the main series:
                endOfAlert = dayjs().subtract(1, 'minute');
              }
              candles.push([
                alertBegTime.valueOf() + this.candleDurationMs,
                ...candleVals,
              ]);

              candles.push([
                endOfAlert.valueOf() - this.candleDurationMs,
                ...candleVals,
              ]);

              return { candles };
            })
            .catch(anyFetchesFailedWrpr),
        );
      } else if (symbolLineParams.kind === 'alert') {
        const reqBegOfDay = reqBeg.tz(ET).startOf('day');
        const reqEndOfDay = reqBegOfDay.add(1, 'day');
        candlesReqs.push(
          this.fetchAPIWithLog<AlertData[]>(
            `v1/alerts?syms=${encodeURIComponent(symbolLineParams.symbol)}&start=${reqBegOfDay.format('YYYY-MM-DD')}&end=${reqEndOfDay.format('YYYY-MM-DD')}`,
          )
            .then((alerts: AlertData[]) => {
              const candles =
                alerts
                  ?.filter(
                    (a) =>
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                      a.category ===
                      HiroAlertCategoryMap[symbolLineParams.alertCategory],
                  )
                  .map((alert): CandleTupleTOHLCV | undefined => {
                    const ts = dayjs(alert.alert_time).valueOf();
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const cumul_delta = alert?.payload?.cumul_delta;
                    if (cumul_delta != null) {
                      return [
                        ts,
                        cumul_delta,
                        cumul_delta,
                        cumul_delta,
                        cumul_delta,
                        0,
                      ];
                    }
                  })
                  .filter(Boolean) ?? [];

              return { candles };
            })
            .catch(anyFetchesFailedWrpr),
        );
      }
    }

    return candlesReqs;
  }

  rangeSubFetches(
    fetchRanges: FetchRange[],
    symbolKind: SymbolLineKind,
    testNow?: Dayjs,
  ): FetchRange[] {
    const now = testNow ?? dayjs();
    const result: FetchRange[] = [];
    for (const { rangeBeg, rangeEnd } of fetchRanges) {
      let reqBeg = rangeBeg;
      for (let watchdog = 0; reqBeg < rangeEnd; ++watchdog) {
        if (watchdog >= 20) {
          console.error('rangeSubFetches failed watchdog limit');
          break;
        }
        let reqEnd;
        const NON_CHUNKED_SYMBOL_KINDS = new Set([
          'key level',
          'alert',
          'oldhiro',
        ]);
        if (
          now.diff(reqBeg, 'day') < 1 &&
          !NON_CHUNKED_SYMBOL_KINDS.has(symbolKind)
        ) {
          if (now.diff(reqBeg, 'hour') < 1) {
            reqEnd =
              now.diff(reqBeg, 'minute') <= 10
                ? rangeEnd
                : // start of the last 10 minute mark
                  rangeEnd
                    .minute(Math.floor(rangeEnd.minute() / 10) * 10)
                    .second(0)
                    .millisecond(0);
          } else {
            reqEnd = rangeEnd.startOf('hour');
          }
        } else {
          reqEnd = reqBeg.add(1, 'day');
        }
        if (reqEnd <= reqBeg) {
          reqEnd = reqEnd.add(1, 'hour');
        }
        if (reqEnd > rangeEnd) {
          reqEnd = rangeEnd;
        }
        result.push({ rangeBeg: reqBeg, rangeEnd: reqEnd });
        reqBeg = reqEnd;
      }
    }
    return result;
  }

  onVisibleRangeChanged(range: VisibleTimeRange): void {
    if (this.currentSession == 'regular') {
      range.from = dayjs(skipNonMarketHours.clampDown(range.from.toDate()));
    }
    this.beginVisibleRange = range.from;
    this.endVisibleRange =
      range.to != null && range.to < dayjs() ? range.to : undefined;
    if (this.currentSession == 'regular' && this.endVisibleRange != null) {
      this.endVisibleRange = dayjs(
        skipNonMarketHours.clampUp(this.endVisibleRange.toDate()),
      );
    }
    const { beginUnixTsInclusive: newWindowBegin } = this.windowFromUnixTs(
      range.from,
    );
    if (newWindowBegin != this.windowBegin) {
      if (this.windowBegin != null && !isFinite(this.rollingWindowMinutes)) {
        this.resetData();
      }
      this.windowBegin = newWindowBegin;
    }

    const visibleRangeUpdate = {
      from: this.beginVisibleRange.valueOf(),
      to: this.endVisibleRange?.valueOf() ?? dayjs().valueOf(),
    };

    if (this.broadCastTimeRangeUpdate != null) {
      this.broadCastTimeRangeUpdate(visibleRangeUpdate);
    }

    // enforce candle duration limits
    const curVisibleDays =
      this.currentSession == 'regular'
        ? skipNonMarketHours.distance(
            this.beginVisibleRange.toDate(),
            this.endVisibleRange?.toDate() ?? new Date(),
          ) / MILLISECONDS_PER_DAY
        : (this.endVisibleRange ?? dayjs()).diff(
            this.beginVisibleRange,
            'days',
            true,
          );

    let minCandleDuration =
      (curVisibleDays * MILLISECONDS_PER_DAY) / MAX_CANDLES_IN_VIEW;

    // +%5 on comparison math due to floating point rounding error when using
    // onIntervalChanged -> onVisibleRangeChanged call path
    minCandleDuration =
      (CANDLE_DURATION_OPTIONS.find(
        (d) => d * 1.05 * 1_000 >= minCandleDuration,
      ) ?? CANDLE_DURATION_OPTIONS[CANDLE_DURATION_OPTIONS.length - 1]) * 1_000;

    let nextCandleDuration = this.userSpecifiedCandleDurationMs;
    if (nextCandleDuration < minCandleDuration) {
      this.updateHiroCtx?.({ candleDuration: minCandleDuration / 1_000 });
      nextCandleDuration = minCandleDuration;
    }
    if (this.candleDurationMs != nextCandleDuration) {
      this.candleDurationMs = nextCandleDuration;
      this.resetData();
    }

    this.refetchAllEnabledSeries();
  }

  onIntervalChanged(candleDurationMs: number): void {
    this.userSpecifiedCandleDurationMs = candleDurationMs;
    if (this.candleDurationMs != candleDurationMs) {
      this.candleDurationMs = candleDurationMs;

      const maxVisibleDays = Math.min(
        MAX_VISIBLE_DAYS,
        (this.candleDurationMs * MAX_CANDLES_IN_VIEW) / MILLISECONDS_PER_DAY,
      );

      const curVisibleDays =
        this.currentSession == 'regular'
          ? skipNonMarketHours.distance(
              this.beginVisibleRange.toDate(),
              this.endVisibleRange?.toDate() ?? new Date(),
            ) / MILLISECONDS_PER_DAY
          : (this.endVisibleRange ?? dayjs()).diff(
              this.beginVisibleRange,
              'days',
              true,
            );

      if (curVisibleDays > maxVisibleDays) {
        const from =
          this.currentSession == 'regular'
            ? dayjs(
                skipNonMarketHours.offset(
                  this.endVisibleRange?.toDate() ?? new Date(),
                  -maxVisibleDays * MILLISECONDS_PER_DAY,
                ),
              )
            : (this.endVisibleRange ?? dayjs()).subtract(
                maxVisibleDays * MILLISECONDS_PER_DAY,
                'milliseconds',
              );
        this.onVisibleRangeChanged({ from, to: this.endVisibleRange });
        this.resetZoomRef?.current();
      }
      this.resetData();
      this.refetchAllEnabledSeries();
    }
  }

  resetData(): void {
    this.lastDataReset = dayjs();
    for (const series of Object.values(this.signalSeries)) {
      series.candles = [];
      series.mutatedCandles = [];
      series.requestedRangeBeg = null;
      series.requestedRangeEnd = null;
      series.latestFetches = Promise.resolve();
      series.windowAccumulator = null;
      series.windowBeginUnixTs = null;
      series.windowAccumulatorTs = null;
    }
    this.refetchAllEnabledSeries();
  }

  refetchAllEnabledSeries() {
    for (const { symbolLineParams } of Object.values(this.signalSeries)) {
      this.enableSignalSeries(symbolLineParams, true);
    }
  }

  setRollingWindowMinutes(rollingWindowMinutes: number): void {
    if (rollingWindowMinutes != this.rollingWindowMinutes) {
      this.rollingWindowMinutes = rollingWindowMinutes;
      this.resetData();
    }
  }

  getCandleChunkTimeMs(timeMs: number): number {
    return (timeMs / this.candleDurationMs) * this.candleDurationMs;
  }
}
